import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ExternalLink, Copy } from "lucide-react";
import { formatAddress, formatTokenAmount } from "@/utils/format";
import { useWalletState } from "@/hooks/useWalletState";
import { toast } from "sonner";
import { bsc, bscTestnet, sepolia, avalanche, avalancheFuji } from 'viem/chains';

interface TokenConfirmationProps {
  tokenAddress: string;
  name: string;
  symbol: string;
  totalSupply: string;
  owner: string;
}

export function TokenConfirmation({ tokenAddress, name, symbol, totalSupply, owner }: TokenConfirmationProps) {
  const { chainId } = useWalletState();
  
  // Get the correct explorer URL and network name based on chain ID
  const getExplorerInfo = () => {
    console.log('TokenConfirmation - Getting explorer info for chainId:', chainId, 'type:', typeof chainId);
    
    switch (chainId) {
      case sepolia.id:
        return {
          url: `https://sepolia.etherscan.io/token/${tokenAddress}`,
          name: 'Etherscan'
        };
      case bscTestnet.id:
        return {
          url: `https://testnet.bscscan.com/token/${tokenAddress}`,
          name: 'BSCScan'
        };
      case bsc.id:
        return {
          url: `https://bscscan.com/token/${tokenAddress}`,
          name: 'BSCScan'
        };
      case 5611: // opBNB Testnet
        return {
          url: `https://testnet.opbnbscan.com/token/${tokenAddress}`,
          name: 'opBNBScan'
        };
      case 204: // opBNB Mainnet
        return {
          url: `https://opbnbscan.com/token/${tokenAddress}`,
          name: 'opBNBScan'
        };
      case avalancheFuji.id:
        return {
          url: `https://testnet.snowtrace.io/token/${tokenAddress}`,
          name: 'Snowtrace'
        };
      case avalanche.id:
        return {
          url: `https://snowtrace.io/token/${tokenAddress}`,
          name: 'Snowtrace'
        };
      case 11124: // Abstract Testnet
        return {
          url: `https://explorer.testnet.abs.xyz/token/${tokenAddress}`,
          name: 'Abstract Explorer'
        };
      case 2741: // Abstract Mainnet
        return {
          url: `https://abscan.org/token/${tokenAddress}`,
          name: 'AbScan'
        };
      case 8453: // Base Mainnet
        return {
          url: `https://basescan.org/token/${tokenAddress}`,
          name: 'BaseScan'
        };
      case 84532: // Base Sepolia
        return {
          url: `https://sepolia.basescan.org/token/${tokenAddress}`,
          name: 'BaseScan'
        };
      case 10: // Optimism Mainnet
        return {
          url: `https://optimistic.etherscan.io/token/${tokenAddress}`,
          name: 'Optimistic Etherscan'
        };
      case 11155420: // Optimism Sepolia
        return {
          url: `https://sepolia-optimism.etherscan.io/token/${tokenAddress}`,
          name: 'Optimistic Etherscan'
        };
      case 42161: // Arbitrum One
        return {
          url: `https://arbiscan.io/token/${tokenAddress}`,
          name: 'Arbiscan'
        };
      case 421614: // Arbitrum Sepolia
        return {
          url: `https://sepolia.arbiscan.io/token/${tokenAddress}`,
          name: 'Arbiscan'
        };
      case 137: // Polygon Mainnet
        return {
          url: `https://polygonscan.com/token/${tokenAddress}`,
          name: 'PolygonScan'
        };
      case 80002: // Polygon Amoy
        return {
          url: `https://amoy.polygonscan.com/token/${tokenAddress}`,
          name: 'PolygonScan'
        };
      case 1328: // Sei Atlantic-2 Testnet
        return {
          url: `https://testnet.seitrace.com/token/${tokenAddress}`,
          name: 'Seitrace'
        };
      case 1329: // Sei Pacific-1 Mainnet
        return {
          url: `https://seitrace.com/token/${tokenAddress}`,
          name: 'Seitrace'
        };
      default:
        console.warn('TokenConfirmation - Unknown chainId, falling back to BSCScan:', chainId);
        return {
          url: `https://bscscan.com/token/${tokenAddress}`,
          name: 'BSCScan'
        };
    }
  };

  const { url: explorerUrl, name: explorerName } = getExplorerInfo();
  
  console.log('TokenConfirmation - Final explorer info:', {
    chainId,
    explorerUrl,
    explorerName,
    tokenAddress
  });

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(tokenAddress);
      toast.success("Address copied to clipboard!");
    } catch (error) {
      toast.error("Failed to copy address");
    }
  };

  const addToWallet = async () => {
    try {
      // Get the provider from Web3Modal
      const provider = (window as any).ethereum;

      if (!provider) {
        throw new Error("No wallet provider found");
      }

      // Wait a bit for any pending requests
      await new Promise(resolve => setTimeout(resolve, 500));

      // Try to add the token using the active provider
      const wasAdded = await provider.request({
        method: 'wallet_watchAsset',
        params: {
          type: 'ERC20',
          options: {
            address: tokenAddress,
            symbol: symbol.slice(0, 11),
            decimals: 18,
            // Only use BSC logo URL if on BSC or opBNB networks
            ...(chainId === bsc.id || chainId === bscTestnet.id || chainId === 204 || chainId === 5611 ? {
              image: `https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/smartchain/assets/${tokenAddress}/logo.png`
            } : {})
          },
        },
      }).catch((error: any) => {
        // If the first method fails, try a different approach for SafePal
        if (provider.isSafePal || error?.code === -32601) {
          return provider.request({
            method: 'wallet_watchAsset',
            params: [{
              type: 'ERC20',
              options: {
                address: tokenAddress,
                symbol: symbol.slice(0, 11),
                decimals: 18,
              },
            }],
          });
        }
        throw error;
      });

      if (wasAdded) {
        toast.success("Token added to wallet!");
      } else {
        throw new Error("Failed to add token");
      }
    } catch (error: any) {
      console.error("Error adding token to wallet:", error);
      
      if (error?.code === 4001) {
        toast.error("You rejected the request to add the token");
      } else if (error?.message?.includes("pending request")) {
        toast.error("Please wait for pending requests to complete");
      } else {
        const getNetworkName = () => {
          switch (chainId) {
            case sepolia.id: return 'Sepolia';
            case bscTestnet.id: return 'BSC Testnet';
            case bsc.id: return 'BSC Mainnet';
            case 5611: return 'opBNB Testnet';
            case 204: return 'opBNB Mainnet';
            case avalancheFuji.id: return 'Avalanche Fuji';
            case avalanche.id: return 'Avalanche';
            case 11124: return 'Abstract Testnet';
            case 2741: return 'Abstract Mainnet';
            case 8453: return 'Base';
            case 84532: return 'Base Sepolia';
            case 10: return 'Optimism';
            case 11155420: return 'Optimism Sepolia';
            case 42161: return 'Arbitrum One';
            case 421614: return 'Arbitrum Sepolia';
            case 137: return 'Polygon';
            case 80002: return 'Polygon Amoy';
            case 1328: return 'Sei Atlantic-2 Testnet';
            case 1329: return 'Sei Pacific-1 Mainnet';
            default: return 'Unknown Network';
          }
        };
        const networkName = getNetworkName();
        toast.error(
          <div className="space-y-2">
            <p>Failed to add token automatically. You can add it manually with these details:</p>
            <p className="text-xs font-mono select-all">{tokenAddress}</p>
            <p className="text-xs">Symbol: {symbol}</p>
            <p className="text-xs">Decimals: 18</p>
            <p className="text-xs">Network: {networkName}</p>
          </div>,
          {
            duration: 8000
          }
        );
      }
    }
  };

  return (
    <Card className="border-2 border-[var(--color-header-accent,#0a74ff)]" style={{
      background: 'linear-gradient(to right, var(--color-background), var(--color-header-accent)10, var(--color-background))'
    }}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <span style={{ color: 'var(--color-header-accent, #0a74ff)' }}>
            Token Created Successfully! 🎉
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-medium mb-1" style={{ color: 'var(--color-secondary, #10b981)' }}>Token Address</h4>
            <div className="flex items-center gap-2">
              <code className="flex-1 font-mono text-sm bg-background/50 p-2 rounded-md break-all min-w-0">
                {tokenAddress}
              </code>
              <Button
                variant="ghost"
                size="icon"
                onClick={copyAddress}
                className="shrink-0"
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="text-sm font-medium mb-1" style={{ color: 'var(--color-secondary, #10b981)' }}>Owner</h4>
              <p className="font-mono text-sm">{formatAddress(owner)}</p>
            </div>
            <div>
              <h4 className="text-sm font-medium mb-1" style={{ color: 'var(--color-secondary, #10b981)' }}>Symbol</h4>
              <p className="font-mono text-sm">{symbol}</p>
            </div>
            <div>
              <h4 className="text-sm font-medium mb-1" style={{ color: 'var(--color-secondary, #10b981)' }}>Name</h4>
              <p className="font-mono text-sm">{name}</p>
            </div>
            <div>
              <h4 className="text-sm font-medium mb-1" style={{ color: 'var(--color-secondary, #10b981)' }}>Total Supply</h4>
              <p className="font-mono text-sm">{formatTokenAmount(totalSupply)} {symbol}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4">
          <Button
            variant="outline"
            className="w-full"
            onClick={() => window.open(explorerUrl, '_blank')}
          >
            <ExternalLink className="mr-2 h-4 w-4" />
            View on {explorerName}
          </Button>
          <Button
            variant="outline"
            className="w-full"
            onClick={addToWallet}
          >
            Add to Wallet
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
